/**
 * @fileoverview CATMS Crew PWA - Application Entry Point
 * @description Ship Crew Attendance & Time Management PWA Application
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { getAccessToken, isPersonalDevice, API_BASE } from './services/api';
import { installNetGate } from './utils/netGate';
import { hasFeature } from './utils/featureFlags';
import { preloadFaceAuth } from './hooks/useFaceAuth';
import { initPressFeedback } from './utils/pressFeedback';
import { applyFontScale } from './utils/fontScale';
import { initSafeArea } from './utils/safeArea';
import { initInstallPrompt, isIOS } from './utils/installPrompt';
import './styles.css';
import { isLoginScreenVisible, PENDING_RELOAD_KEY } from './utils/pendingReload';

// 船内Wi-Fi（インターネットに出られない）につながったままの沖で、通信が失敗するまで数分ずつ待って
// 起動・ログイン・打刻に15分かかっていた（2026-10-19 紺星丸）。サーバーに届かないと分かったら
// 待たずに圏外として扱う見張りを、どの通信よりも先に入れる。会社ごとのフラグで段階的に有効化する。
if (hasFeature('crew_fast_offline')) installNetGate(API_BASE);
// 起動中に設定が届いた場合も、その場で見張りを入れる（船内端末はアプリを開きっぱなしで使うため、
// 次の起動を待たずに効かせる）。installNetGate は2回目以降は何もしない。
window.addEventListener('catms-features-updated', () => { if (hasFeature('crew_fast_offline')) installNetGate(API_BASE); });
// 端末で選んだ文字の大きさを描画前に反映する（切り替え時のちらつきを防ぐ）
applyFontScale();
// ノッチ／カメラ穴でヘッダーが隠れないよう、上に空ける高さを先に決めておく
initSafeArea();
// 「ホーム画面に追加」のイベントは読み込み直後に一度だけ来るので、最初に捕まえておく
initInstallPrompt();
// iPhone/iPad は viewport の拡大禁止を無視して、2本指で画面全体を拡大できてしまう（拡大したまま引っ張れて操作しにくい）。
// 画面全体の拡大だけ止める。証明書プレビューの拡大は touch イベントで自前実装しているので影響しない。
if (isIOS()) {
  const stop = (e: Event) => e.preventDefault();
  for (const t of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(t, stop, { passive: false });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// 顔認証AIモデルの起動時プリロード（カメラ起動を待たずに読込＋GPUウォームアップを開始し、
// 起動直後の初回顔認証の待ち時間を短縮する）。
// ただし個人用アプリ（?app=personal＝パスワードログイン既定・顔認証を使わない）では行わない。
// 実機のWebGLバックエンドで warmup(detectSingleFace) がメインスレッドを固め、
// ログイン画面が「静止画のように無反応」になるのを防ぐ。管理端末（顔認証既定）のみ実行する。
if (!isPersonalDevice()) preloadFaceAuth();

// 全ボタンの「押した感」を有効化（inline style を持つボタンでも確実に効くJS方式）。
initPressFeedback();

// Service Worker registration
if ('serviceWorker' in navigator) {
  // 新バージョンの SW が制御権を取得したら自動リロードして最新コードを反映する。
  // （ページ読込時点で既に SW 制御下＝更新のケースのみ。初回インストール時は
  //   controller が無いのでリロードしない＝無限リロード防止）
  if (navigator.serviceWorker.controller) {
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      // 共有端末は再読み込みでログアウトされる。電波が戻った瞬間に新しい版を見つけて
      // 打刻中の人を締め出さないよう、ログイン画面が出ているときだけ再読み込みし、
      // ログイン中は「次にログイン画面へ戻ったとき」に回す（LoginPage が拾う）。
      if (!isLoginScreenVisible()) {
        try { sessionStorage.setItem(PENDING_RELOAD_KEY, '1'); } catch { /* 保存できなければ次回起動で反映 */ }
        return;
      }
      reloading = true;
      window.location.reload();
    });
  }

  window.addEventListener('load', async () => {
    try {
      // updateViaCache:'none' で sw.js と importScripts 取り込み分を常に再検証し、
      // 更新チェックがHTTPキャッシュで空振りしないようにする（自動更新の信頼性向上）。
      const reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });

      // 起動時とフォアグラウンド復帰時に更新チェック（新 sw.js があれば skipWaiting で
      // 即時有効化 → controllerchange → 自動リロード。再インストール不要）
      reg.update().catch(() => {});
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reg.update().catch(() => {});
      });

      // Periodic Background Sync の登録（対応ブラウザのみ）
      if ('periodicSync' in reg) {
        try {
          await (reg as unknown as { periodicSync: { register: (tag: string, opts: object) => Promise<void> } })
            .periodicSync.register('gps-location-sync', { minInterval: 60 * 1000 });
        } catch { /* 権限なし等は無視 */ }
      }

      // SW からの GPS 取得依頼を受け取りポストバック
      navigator.serviceWorker.addEventListener('message', async (event) => {
        if (event.data?.type !== 'REQUEST_GPS') return;
        if (!navigator.geolocation) return;
        const token = getAccessToken();
        const vesselId = localStorage.getItem('catms_last_vessel_id');
        if (!token) return;
        // 船内端末（共有端末）の識別トークンを付ける。サーバーはこれで「船内端末の測位か」を判定し、
        // 地図の船位置は船内端末の測位だけを使う（個人スマホの位置で船位置が汚れるのを防ぐ）。
        // 個人スマホはトークンを持たないので、GPSは勤怠用に記録されるが船位置には使われない。
        const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
        try {
          const offlineTok = localStorage.getItem('catms_offline_token');
          const deviceTok = localStorage.getItem('catms_device_token');
          if (offlineTok) headers['X-Offline-Token'] = offlineTok;
          if (deviceTok) headers['X-Device-Token'] = deviceTok;
        } catch { /* localStorage 不可時はトークン無しで送る */ }
        navigator.geolocation.getCurrentPosition(pos => {
          fetch('/api/gps/location', {
            method: 'POST',
            headers,
            body: JSON.stringify({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy, speed: pos.coords.speed, heading: pos.coords.heading, vessel_id: vesselId }),
          }).catch(() => {});
        }, () => {}, { timeout: 10000, maximumAge: 30000, enableHighAccuracy: false });
      });
    } catch (err) {
      console.warn('SW registration failed:', err);
    }
  });
}
